import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { Form } from "react-bootstrap";
import api, { BASE_URL } from "../../../Utils/api";
import Modal from "react-bootstrap/Modal";
import { FaGripVertical } from "react-icons/fa";
import { Salvar, Cancelar } from "../../Generals/buttons";
import { GlobalContext } from "../../../Utils/GlobalContext";
import "./ConfiguracoesCamposModal.css";


const BADGE_ORIGEM = {
  C: { label: "Customizado", bg: "#e0edff", color: "#1d4ed8" },
  P: { label: "Padrão",      bg: "#f0fdf4", color: "#15803d" },
};

const ConfiguracoesCamposModal = ({ show, handleClose, configuracao, fetchConfiguracoes }) => {
  const { darkMode, globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");

  const bg         = "var(--surface-card)";
  const bgHead     = "var(--surface-sunken)";
  const borda      = "var(--border-default)";
  const texto      = "var(--text-body)";
  const textoMuted = "var(--text-muted)";
  const bgLinha    = (idx, isDragOver, isDragging) =>
    isDragOver  ? "var(--bs-primary-bg-subtle, #cfe2ff)" :
    isDragging  ? "var(--surface-sunken)" :
    idx % 2 === 0 ? "var(--surface-card)" : "var(--table-stripe)";

  const [campos, setCampos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [temIndices, setTemIndices] = useState(true);
  const [temFuncionarios, setTemFuncionarios] = useState(true);
  const [temTiposPagamento, setTemTiposPagamento] = useState(true);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragNode = useRef(null);

  // Mesmo padrão do DocumentosModal: busca campos padrão + customizados separadamente e mescla.
  const fetchCampos = useCallback(async (idTipoConfiguracao, idTipoDocumento, idEnt) => {
    if (!idTipoConfiguracao) { setCampos([]); return; }
    setLoading(true);
    setTemIndices(true); // optimistic enquanto carrega
    const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } };
    try {
      // 1. Campos padrão de configuracoes_campos (exclui customizada_*)
      const { data: dataStd } = await api.get(
        `${BASE_URL}/api/configuracaoCampos/ListarConfiguracoesCamposPorTipo/${idTipoConfiguracao}`,
        authHeader
      );
      const camposFixos = (Array.isArray(dataStd?.data) ? dataStd.data : [])
        .filter(c => !String(c.nome_campo || "").trim().startsWith("customizada_"))
        .map(c => ({ ...c, _custom: false }));

      // 2. Campos customizados de campos_customizados (inclui globais id_tipo_documento IS NULL)
      // Quando id_tipo_documento é null (TCC global), passa 0 para retornar apenas os globais.
      let camposCustom = [];
      if (idEnt) {
        const idTdParam = idTipoDocumento != null ? idTipoDocumento : 0;
        try {
          const { data: dataCustom } = await api.get(
            `${BASE_URL}/api/camposCustomizados/ListarCamposPorTipoDocumento/${idEnt}/${idTdParam}`,
            authHeader
          );
          camposCustom = (Array.isArray(dataCustom?.data) ? dataCustom.data : []).map(c => ({
            id:            c.id,
            nome_campo:    c.nome_campo,
            descricao:     c.descricao,
            visivel:       c.visivel  ?? "S",
            obrigatorio:   c.obrigatorio ?? "N",
            ordem:         c.ordem != null ? Number(c.ordem) : 999,
            origem:        "C",
            _custom:       true,
            _id_tipo_documento: c.id_tipo_documento ?? idTipoDocumento ?? null,
          }));
        } catch (e) {
          console.warn("Campos customizados não carregados:", e.message);
        }
      }

      // 3. Verificar se existem índices de correção, funcionários e tipos de pagamento.
      // Sem registros, esses campos não podem ser tornados visíveis.
      let temIndicesData = true;
      let temFuncionariosData = true;
      let temTiposPagamentoData = true;
      if (idEnt) {
        try {
          const { data: indicesData } = await api.get(
            `${BASE_URL}/api/indicesCorrecao/ListarIndicesCorrecao/${idEnt}`,
            authHeader
          );
          temIndicesData = Array.isArray(indicesData?.data) && indicesData.data.length > 0;
        } catch (e) {
          temIndicesData = false;
        }
        try {
          const { data: funcionariosData } = await api.get(
            `${BASE_URL}/api/funcionarios/ListarFuncionarios/${idEnt}`,
            authHeader
          );
          temFuncionariosData = Array.isArray(funcionariosData?.data) && funcionariosData.data.length > 0;
        } catch (e) {
          temFuncionariosData = false;
        }
        try {
          const { data: tiposPagamentoData } = await api.get(
            `${BASE_URL}/api/tiposPagamento/ListarTiposPagamento/${idEnt}`,
            authHeader
          );
          temTiposPagamentoData = Array.isArray(tiposPagamentoData?.data) && tiposPagamentoData.data.length > 0;
        } catch (e) {
          temTiposPagamentoData = false;
        }
      }
      setTemIndices(temIndicesData);
      setTemFuncionarios(temFuncionariosData);
      setTemTiposPagamento(temTiposPagamentoData);

      // 4. Mescla: campos padrão primeiro, depois customizados não duplicados por nome_campo.
      // Quando não há registros, força campos relacionados para invisível/não-obrigatório.
      const nomesFixos = new Set(camposFixos.map(c => c.nome_campo));
      const customNovos = camposCustom.filter(c => !nomesFixos.has(c.nome_campo));
      const merged = [...camposFixos, ...customNovos]
        .sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0))
        .map((c) => {
          if (!temIndicesData && c.nome_campo === "id_indice_correcao") {
            return { ...c, visivel: "N", obrigatorio: "N" };
          }
          if (!temFuncionariosData && c.nome_campo === "id_funcionario") {
            return { ...c, obrigatorio: "N" };
          }
          if (!temTiposPagamentoData && c.nome_campo === "id_tipo_pagamento") {
            return { ...c, visivel: "N", obrigatorio: "N" };
          }
          return c;
        });
      setCampos(merged);
    } catch (err) {
      console.error("Erro ao buscar campos:", err);
      setCampos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (configuracao) fetchCampos(configuracao.id, configuracao.id_tipo_documento, entidadeID);
    else setCampos([]);
  }, [configuracao, entidadeID, fetchCampos]);

  // ── Toggle visível / obrigatório ─────────────────────────────────────────
  const handleSwitch = (id, field) => {
    setCampos((prev) => {
      const alvo = prev.find((c) => c.id === id);
      if (!alvo) return prev;
      // Impede tornar campos visíveis quando não há registros configurados.
      if (field === "visivel" && !temIndices && alvo.nome_campo === "id_indice_correcao") return prev;
      if (field === "visivel" && !temTiposPagamento && alvo.nome_campo === "id_tipo_pagamento") return prev;
      const novo = alvo[field] === "S" ? "N" : "S";

      const isFornecedor  = (c) => /fornecedor/i.test(c.nome_campo || "") || /fornecedor/i.test(c.descricao || "");
      const isFuncionario = (c) => /funcionari/i.test(c.nome_campo || "") || /funcionari/i.test(c.descricao || "");

      return prev.map((c) => {
        const u = { ...c };
        if (c.id === id) {
          u[field] = novo;
          if (field === "visivel" && novo === "N") u.obrigatorio = "N";
          return u;
        }
        if (field === "visivel") {
          if (isFornecedor(alvo) && novo === "N" && isFuncionario(c)) { u.visivel = "N"; u.obrigatorio = "N"; }
          if (isFuncionario(alvo) && novo === "S" && isFornecedor(c)) { u.visivel = "S"; }
        }
        return u;
      });
    });
  };

  // ── Drag-and-drop ────────────────────────────────────────────────────────
  const onDragStart = (e, idx) => {
    dragNode.current = e.currentTarget;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Ghost image transparente para evitar o clone feio do browser
    const ghost = document.createElement("div");
    ghost.style.position = "absolute";
    ghost.style.top = "-9999px";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== overIdx) setOverIdx(idx);
  };

  const onDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { endDrag(); return; }
    setCampos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      next.forEach((c, i) => { c.ordem = i + 1; });
      return next;
    });
    endDrag();
  };

  const endDrag = () => { setDragIdx(null); setOverIdx(null); dragNode.current = null; };

  // ── Salvar ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } };
    const camposStd    = campos.filter(c => !c._custom);
    const camposCustom = campos.filter(c => c._custom);
    try {
      const promises = [];
      if (camposStd.length > 0) {
        promises.push(api.put(
          `${BASE_URL}/api/configuracaoCampos/EditarConfiguracoesCampos/${configuracao.id}`,
          { campos: camposStd.map(({ id, visivel, obrigatorio, ordem }) => ({ id, visivel, obrigatorio, ordem })) },
          authHeader
        ));
      }
      if (camposCustom.length > 0 && entidadeID) {
        promises.push(api.put(
          `${BASE_URL}/api/camposCustomizados/EditarVisibilidadeBatch/${entidadeID}`,
          { campos: camposCustom.map(({ id, _id_tipo_documento, visivel, obrigatorio, ordem }) => ({
              id_campo_customizado: id,
              id_tipo_documento:    _id_tipo_documento,
              visivel,
              obrigatorio,
              ordem,
          })) },
          authHeader
        ));
      }
      await Promise.all(promises);
      fetchConfiguracoes();
      handleClose();
    } catch (err) {
      console.error("Erro ao salvar:", err);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg" dialogClassName="modal-dialog-top">
      <Modal.Header closeButton style={{ background: bgHead, borderColor: borda, paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: texto }}>
          Configurações de Campos
          {configuracao?.nome && (
            <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 400, color: textoMuted, marginTop: 2 }}>
              {configuracao.nome}
              {configuracao.descricao ? ` — ${configuracao.descricao}` : ""}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={`cc-modal-body${darkMode ? " dark" : ""}`} style={{ padding: 0, maxHeight: "65vh", overflowY: "auto", background: bg }}>
        {loading ? (
          <div className="text-center py-5" style={{ fontSize: "0.85rem", color: textoMuted }}>
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Carregando campos...
          </div>
        ) : campos.length === 0 ? (
          <div className="text-center py-5" style={{ fontSize: "0.85rem", color: textoMuted }}>
            Nenhum campo encontrado.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
            <thead>
              <tr style={{ background: bgHead, borderBottom: `2px solid ${borda}`, position: "sticky", top: 0, zIndex: 1 }}>
                <th style={{ width: 32, padding: "8px 6px" }} />
                <th style={{ padding: "8px 12px", fontWeight: 600, color: texto }}>Campo</th>
                <th style={{ padding: "8px 12px", fontWeight: 600, color: texto }}>Origem</th>
                <th style={{ padding: "8px 12px", fontWeight: 600, color: texto, textAlign: "center" }}>Visível</th>
                <th style={{ padding: "8px 12px", fontWeight: 600, color: texto, textAlign: "center" }}>Obrigatório</th>
              </tr>
            </thead>
            <tbody>
              {campos.map((campo, idx) => {
                const badge = BADGE_ORIGEM[campo.origem] || {};
                const isDragOver = idx === overIdx;
                const isDragging = idx === dragIdx;
                const semIndices = campo.nome_campo === "id_indice_correcao" && !temIndices;
                const semFuncionarios = campo.nome_campo === "id_funcionario" && !temFuncionarios;
                const semTiposPagamento = campo.nome_campo === "id_tipo_pagamento" && !temTiposPagamento;
                const temRestricao = semIndices || semTiposPagamento;
                return (
                  <tr
                    key={campo.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, idx)}
                    onDragOver={(e) => onDragOver(e, idx)}
                    onDrop={(e) => onDrop(e, idx)}
                    onDragEnd={endDrag}
                    style={{
                      background: bgLinha(idx, isDragOver, isDragging),
                      transition: "background 0.1s",
                      borderBottom: `1px solid ${borda}`,
                      opacity: isDragging ? 0.5 : 1,
                    }}
                  >
                    <td style={{ padding: "6px 8px", cursor: "grab", color: textoMuted, textAlign: "center", userSelect: "none" }}>
                      <FaGripVertical />
                    </td>

                    <td style={{ padding: "6px 12px", color: texto, fontWeight: 500 }}>
                      {campo.descricao || campo.nome_campo}
                      {semIndices && (
                        <span style={{ display: "block", fontSize: "0.71rem", fontWeight: 400, color: textoMuted, marginTop: 1 }}>
                          Nenhum índice configurado para esta entidade
                        </span>
                      )}
                      {semFuncionarios && (
                        <span style={{ display: "block", fontSize: "0.71rem", fontWeight: 400, color: textoMuted, marginTop: 1 }}>
                          Nenhum funcionário configurado para esta entidade
                        </span>
                      )}
                      {semTiposPagamento && (
                        <span style={{ display: "block", fontSize: "0.71rem", fontWeight: 400, color: textoMuted, marginTop: 1 }}>
                          Nenhum tipo de pagamento configurado para esta entidade
                        </span>
                      )}
                    </td>

                    <td style={{ padding: "6px 12px" }}>
                      {badge.label && (
                        <span style={{
                          display: "inline-block", fontSize: "0.72rem", fontWeight: 600,
                          padding: "2px 7px", borderRadius: 20,
                          background: badge.bg, color: badge.color,
                        }}>
                          {badge.label}
                        </span>
                      )}
                    </td>

                    <td style={{ padding: "6px 12px", textAlign: "center" }}>
                      <Form.Check
                        type="switch"
                        id={`visivel-${campo.id}`}
                        checked={campo.visivel === "S"}
                        onChange={() => handleSwitch(campo.id, "visivel")}
                        disabled={temRestricao}
                        title={
                          semIndices
                            ? "Configure pelo menos um Índice de Correção antes de habilitar este campo"
                            : semTiposPagamento
                            ? "Configure pelo menos um Tipo de Pagamento antes de habilitar este campo"
                            : undefined
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </td>

                    <td style={{ padding: "6px 12px", textAlign: "center" }}>
                      <Form.Check
                        type="switch"
                        id={`obrigatorio-${campo.id}`}
                        checked={campo.obrigatorio === "S"}
                        onChange={() => handleSwitch(campo.id, "obrigatorio")}
                        disabled={campo.visivel !== "S" || semFuncionarios}
                        title={semFuncionarios ? "Configure pelo menos um Funcionário antes de tornar este campo obrigatório" : undefined}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Modal.Body>

      <Modal.Footer className="modal-footer-padrao" style={{ background: bgHead, borderColor: borda }}>
        <div className="modal-footer-acoes-esquerda">
          {campos.length > 0 && (
            <span style={{ fontSize: "0.78rem", color: textoMuted }}>
              {campos.length} campo{campos.length !== 1 ? "s" : ""} · arraste para reordenar
            </span>
          )}
        </div>
        <div className="modal-footer-acoes-direita">
          <Cancelar onClick={handleClose} />
          <Salvar onClick={handleSave} />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfiguracoesCamposModal;
